import React from 'react';
import { PieChart, Pie, Label, Tooltip, Cell, Legend } from 'recharts';
import { observer } from 'mobx-react-lite';

import uniq from 'lodash/uniq';

import { CancelType } from '@vromo/models';

import { ChartColours } from '../../ChartColours';
import ObservableAutoSizer from '@vromo/ui/components/ObservableAutoSizer';
import { Job } from 'models';

interface CustomLabelProps {
    viewBox?: { cx: number; cy: number };
    value1: number;
    value2: string;
}

const CustomLabel = ({ viewBox, value1, value2 }: CustomLabelProps) => {
    const { cx = 0, cy = 0 } = viewBox || {};
    return (
        <>
            <text x={cx} y={cy - 5} textAnchor="middle">
                <tspan alignmentBaseline="middle" fontSize="18">
                    {value1}
                </tspan>
            </text>
            <text x={cx} y={cy + 10} textAnchor="middle">
                <tspan alignmentBaseline="middle" fontSize="12">
                    {value2}
                </tspan>
            </text>
        </>
    );
};

interface CancelReasonChartProps {
    cancelledJobs: Job[];
}

export default observer<CancelReasonChartProps>(function CancelReasonChart({ cancelledJobs }) {
    const cancelIds = uniq(
        cancelledJobs.map((j) => (j.cancelled?.reason?.id ? j.cancelled?.reason?.id : 25199))
    );

    const formatTooltip = (cancelNumber: number, totalCancelled: number, props: any) => {
        const formattedPercentage = `${cancelNumber} ${cancelNumber > 1 ? 'Jobs' : 'Job'}  (${(
            (cancelNumber / totalCancelled) *
            100
        ).toFixed(1)}%)`;
        return [formattedPercentage, CancelType[props.payload.payload.reason]];
    };

    const data = cancelIds.map((id) => {
        const reasonCount = cancelledJobs.reduce((acc, j) => {
            const reason = j.cancelled?.reason?.id ? j.cancelled?.reason?.id : 25199;
            return reason == id ? acc + 1 : acc;
        }, 0);
        return { reason: id, value: reasonCount };
    });

    if (cancelledJobs.length === 0) {
        return (
            <div
                style={{
                    display: 'flex',
                    height: '80%',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                No cancelled jobs to show.
            </div>
        );
    }

    return (
        <ObservableAutoSizer customChildren={({ width }: { width: number }) => {
            const isMobileView = width < 400;

            return (
                <PieChart width={width} height={220}>
                    <Pie dataKey="value" data={data} outerRadius={80} cy={110} innerRadius={50}>
                        <Label
                            width={50}
                            position="center"
                            content={
                                <CustomLabel
                                    value1={cancelledJobs.length}
                                    value2={`Job${cancelledJobs.length > 1 ? 's' : ''
                                        } Cancelled`}
                                />
                            }
                        />
                        {data.map((entry, index) => (
                            <Cell
                                strokeWidth={0}
                                key={entry.reason}
                                fill={ChartColours[index % ChartColours.length]}
                            />
                        ))}
                    </Pie>
                    <Tooltip
                        labelFormatter={(label) => {
                            return label;
                        }}
                        formatter={(value: number, name: string, props: any) =>
                            formatTooltip(value, cancelledJobs.length, props)
                        }
                    />
                    <Legend
                        wrapperStyle={{ display: isMobileView ? 'none' : 'absolute' }}
                        layout="vertical"
                        align={isMobileView ? 'center' : 'right'}
                        verticalAlign={isMobileView ? 'bottom' : 'top'}
                        iconType="square"
                        formatter={(value, entry: any) => {
                            return (
                                entry.payload && <span>{CancelType[entry.payload.reason]}</span>
                            );
                        }}
                    />
                </PieChart>
            );
        }} />
    );
});
